import { useState, useEffect, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ClientCombobox } from "@/components/shared/ClientCombobox";
import { WhatsAppContactPicker } from "@/components/shared/WhatsAppContactPicker";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Client, EfromClient } from "@/services/client";
import { streamingServiceApi, PricePlan } from "@/services/streaming-service";
import {
  subscriptionService,
  CreateSubscriptionRequest,
  ESubType,
  ESubscriptionType,
  EownerType,
} from "@/services/subscription";
import { Loader2, Calendar, User, CreditCard, Wallet, CircleCheck, UserPlus, HelpCircle } from "lucide-react";
import { EPaymentMethod, EPaymentStatus } from "@/services/payment";
import { formatCurrency } from "@/lib/utils";

interface AssignSubscriptionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slotId: number;
  slotName: string;
  streamingAccountId: number;
  serviceId: number;
}

export function AssignSubscriptionModal({
  open,
  onOpenChange,
  slotId,
  slotName,
  streamingAccountId,
  serviceId,
}: AssignSubscriptionModalProps) {
  const queryClient = useQueryClient();

  const [clientMode, setClientMode] = useState<"existing" | "new">("existing");
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [newClientData, setNewClientData] = useState({
    name: "",
    phone: "",
    from: EfromClient.WEB,
  });

  const [selectedPricePlanId, setSelectedPricePlanId] = useState<number | undefined>(undefined);
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [paymentMethod, setPaymentMethod] = useState<EPaymentMethod>(EPaymentMethod.TRANSFER);
  const [paymentStatus, setPaymentStatus] = useState<EPaymentStatus>(EPaymentStatus.COMPLETED);
  const [clientId, setClientId] = useState<number | undefined>(undefined);
  const [isNotify, setIsNotify] = useState(false);

  useEffect(() => {
    if (open) {
      setStartDate(new Date().toISOString().split("T")[0]);
      setIsNotify(false);
    }
  }, [open]);

  // Obtener servicio de streaming para obtener los price plans
  const { data: serviceResponse, isLoading: isLoadingService } = useQuery({
    queryKey: ["streaming-service", serviceId],
    queryFn: () => streamingServiceApi.getById(serviceId),
    enabled: open && !!serviceId,
  });

  const service = serviceResponse?.data;
  const pricePlans = useMemo(() => {
    if (!service?.pricePlans) return [];
    return service.pricePlans.filter(
      (plan: PricePlan) => plan.status === "ACTIVE" && plan.ownerType == EownerType.CLIENT
    );
  }, [service]);

  // Mutation para crear suscripción
  const createMutation = useMutation({
    mutationFn: (data: CreateSubscriptionRequest) =>
      subscriptionService.create(data),
    onSuccess: () => {
      toast.success("Suscripción asignada exitosamente");
      queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
      queryClient.invalidateQueries({ queryKey: ["streaming-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      handleClose();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleClose = () => {
    setClientMode("existing");
    setSelectedClient(null);
    setClientId(undefined);
    setNewClientData({ name: "", phone: "", from: EfromClient.WEB });
    setSelectedPricePlanId(undefined);
    setStartDate(new Date().toISOString().split("T")[0]);
    setPaymentMethod(EPaymentMethod.TRANSFER);
    setPaymentStatus(EPaymentStatus.COMPLETED);
    setIsNotify(false);
    onOpenChange(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (clientMode === "existing") {
      if (!clientId) {
        toast.error("Selecciona un cliente");
        return;
      }
    } else {
      if (!newClientData.name.trim()) {
        toast.error("Ingresa el nombre del cliente");
        return;
      }
      if (!newClientData.phone.trim()) {
        toast.error("Ingresa el teléfono del cliente");
        return;
      }
    }

    if (!selectedPricePlanId) {
      toast.error("Selecciona un plan de precio");
      return;
    }

    if (!startDate) {
      toast.error("Selecciona una fecha de inicio");
      return;
    }

    const requestData: CreateSubscriptionRequest = {
      ...(clientMode === "existing"
        ? { clientId: Number(clientId) }
        : { newClient: newClientData }),
      type: ESubType.SINGLE,
      startDate,
      paymentMethod,
      paymentStatus,
      isNotify,
      subscriptionItems: [
        {
          serviceId: Number(serviceId),
          streamingAccountId: Number(streamingAccountId),
          subscriptionType: ESubscriptionType.SLOT,
          pricePlanId: Number(selectedPricePlanId),
          slotId: slotId ? Number(slotId) : undefined,
        },
      ],
    };

    createMutation.mutate(requestData);
  };

  const selectedPricePlan = pricePlans.find(
    (p: PricePlan) => p.id === selectedPricePlanId
  );

  // Calcular fecha de finalización
  const endDate = useMemo(() => {
    if (!selectedPricePlan || !startDate) return null;
    const start = new Date(startDate);
    start.setDate(start.getDate() + selectedPricePlan.duration);
    return start.toISOString().split("T")[0];
  }, [selectedPricePlan, startDate]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-[700px] lg:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-lg">Asignar Suscripción</DialogTitle>
              <DialogDescription className="text-sm">
                Slot: <strong>{slotName}</strong>
                {service && (
                  <span className="ml-2 text-muted-foreground">
                    ({service.icon} {service.name})
                  </span>
                )}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {isLoadingService ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Cliente - Tabs para existente o nuevo */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1.5 text-sm">
                  <User className="h-3.5 w-3.5" />
                  Cliente
                </Label>
                <Tabs value={clientMode} onValueChange={(v) => setClientMode(v as "existing" | "new")}>
                  <TabsList className="h-7">
                    <TabsTrigger value="existing" className="text-xs px-2 py-1 h-6 gap-1">
                      <User className="h-3 w-3" />
                      Existente
                    </TabsTrigger>
                    <TabsTrigger value="new" className="text-xs px-2 py-1 h-6 gap-1">
                      <UserPlus className="h-3 w-3" />
                      Nuevo
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {clientMode === "existing" ? (
                <ClientCombobox
                  value={selectedClient}
                  onChange={(client) => {
                    setSelectedClient(client);
                    setClientId(client ? Number(client.id) : undefined);
                  }}
                />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 p-2 bg-muted/30 rounded-md border border-dashed">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Nombre"
                      className="h-9 flex-1 min-w-0"
                      value={newClientData.name}
                      onChange={(e) => setNewClientData((prev) => ({ ...prev, name: e.target.value }))}
                    />
                    <WhatsAppContactPicker
                      className="h-9 w-9 shrink-0 text-success"
                      onSelect={({ name, phone }) =>
                        setNewClientData((prev) => ({ ...prev, name, phone }))
                      }
                    />
                  </div>
                  <Input
                    placeholder="Teléfono"
                    className="h-9"
                    value={newClientData.phone}
                    onChange={(e) => setNewClientData((prev) => ({ ...prev, phone: e.target.value }))}
                  />
                  <Select
                    value={newClientData.from}
                    onValueChange={(value) => setNewClientData((prev) => ({ ...prev, from: value as EfromClient }))}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Origen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={EfromClient.WEB}>Web</SelectItem>
                      <SelectItem value={EfromClient.REFERRAL}>Referido</SelectItem>
                      <SelectItem value={EfromClient.FACEBOOK}>Facebook</SelectItem>
                      <SelectItem value={EfromClient.INSTAGRAM}>Instagram</SelectItem>
                      <SelectItem value={EfromClient.ADVERTISEMENT}>Publicidad</SelectItem>
                      <SelectItem value={EfromClient.OTHER}>Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Fila de campos: Plan, Fecha, Método, Estado */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {/* Plan de precio */}
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1 text-muted-foreground">
                  <CreditCard className="h-3 w-3" />
                  Plan
                </Label>
                <Select
                  value={selectedPricePlanId?.toString()}
                  onValueChange={(value) => setSelectedPricePlanId(Number(value))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {pricePlans.length === 0 ? (
                      <div className="p-2 text-sm text-muted-foreground text-center">
                        Sin planes
                      </div>
                    ) : (
                      pricePlans.map((plan: PricePlan) => (
                        <SelectItem key={plan.id} value={plan.id.toString()}>
                          {formatCurrency(plan.price)} / {parseInt(plan.duration.toString())}d
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Fecha de inicio */}
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1 text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  Inicio
                </Label>
                <Input
                  type="date"
                  className="h-9"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              {/* Método de pago */}
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1 text-muted-foreground">
                  <Wallet className="h-3 w-3" />
                  Método
                </Label>
                <Select
                  value={paymentMethod}
                  onValueChange={(value) => setPaymentMethod(value as EPaymentMethod)}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Método" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={EPaymentMethod.TRANSFER}>Transferencia</SelectItem>
                    <SelectItem value={EPaymentMethod.CASH}>Efectivo</SelectItem>
                    <SelectItem value={EPaymentMethod.NEQUI}>Nequi</SelectItem>
                    <SelectItem value={EPaymentMethod.DAVIPLATA}>Daviplata</SelectItem>
                    <SelectItem value={EPaymentMethod.BANCOLOMBIA}>Bancolombia</SelectItem>
                    <SelectItem value={EPaymentMethod.PAYPAL}>Paypal</SelectItem>
                    <SelectItem value={EPaymentMethod.BINANCE}>Binance</SelectItem>
                    <SelectItem value={EPaymentMethod.YAPE}>Yape</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Estado del pago */}
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1 text-muted-foreground">
                  <CircleCheck className="h-3 w-3" />
                  Estado
                </Label>
                <Select
                  value={paymentStatus}
                  onValueChange={(value) => setPaymentStatus(value as EPaymentStatus)}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={EPaymentStatus.COMPLETED}>Completado</SelectItem>
                    <SelectItem value={EPaymentStatus.PENDING}>Pendiente</SelectItem>
                    <SelectItem value={EPaymentStatus.CANCELLED}>Cancelado</SelectItem>
                    <SelectItem value={EPaymentStatus.REFUNDED}>Reembolsado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Notificar al cliente */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="notify-client-assign"
                checked={isNotify}
                onCheckedChange={(checked) => setIsNotify(checked === true)}
              />
              <Label htmlFor="notify-client-assign" className="text-sm font-normal cursor-pointer">
                Notificar al cliente
              </Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <HelpCircle className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-[260px] text-sm">
                  Al asignar la suscripción, se le enviará al cliente la información de la cuenta (correo y contraseña).
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Resumen + Botones en la misma fila */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-2 border-t">
              {selectedPricePlan && (selectedClient || newClientData.name) ? (
                <div className="flex-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm px-2 py-1.5 bg-primary/5 rounded-md">
                  <span className="text-muted-foreground">
                    Precio: <strong className="text-foreground">${parseInt(selectedPricePlan.price.toString()).toFixed(2)}</strong>
                  </span>
                  <span className="text-muted-foreground">
                    Duración: <strong className="text-foreground">{selectedPricePlan.duration}d</strong>
                  </span>
                  {endDate && (
                    <span className="text-muted-foreground">
                      Fin: <strong className="text-foreground">{endDate}</strong>
                    </span>
                  )}
                </div>
              ) : (
                <div className="flex-1" />
              )}
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" size="sm" onClick={handleClose}>
                  Cancelar
                </Button>
                <Button type="submit" size="sm" disabled={createMutation.isPending}>
                  {createMutation.isPending && (
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  )}
                  Asignar
                </Button>
              </div>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
